import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import React from 'react';
import { DebugPanel } from '@app/components';
import { useAccountabilityPartnerPairingEditorController } from '@components/Admin/AccountabilityPartnerPairingEditor/UseAccountabilityPartnerPairingEditorController';
import { DataGrid } from '@mui/x-data-grid';

interface AccountabilityPartnerPairingEditorProps {
  groupId: number;
  open: boolean;
  closeDialog: () => void;
}

export const AccountabilityPartnerPairingEditor: React.FC<AccountabilityPartnerPairingEditorProps> = ({
  groupId,
  open,
  closeDialog,
}) => {
  const {
    isLoading,
    currentAccountabilityPartnersWithNames,
    rows,
    participants,
    deleteAllPartners,
    deletePartnerRecordById,
    handleAddRow,
    handleCancelNewRow,
    handleSaveNewRow,
    updateNewRowParticipant,
    updateNewRowPartner,
  } = useAccountabilityPartnerPairingEditorController(groupId);

  return (
    <Dialog open={open} fullWidth={true} maxWidth={'lg'} onClose={closeDialog} aria-labelledby="form-dialog-title">
      {isLoading && <CircularProgress variant={'indeterminate'}></CircularProgress>}
      <DialogTitle>Accountability Partner Pairing Editor</DialogTitle>
      <DialogContent>
        <Box pb={2}>
          <Accordion variant={'elevation'}>
            <AccordionSummary expandIcon={<KeyboardArrowDown />}>More Info</AccordionSummary>
            <AccordionDetails>
              <Typography>
                When setting accountability partners make sure of the following:
                <ol>
                  <li>Each person has an accountability partner.</li>
                  <li>Each person has a different accountability partner.</li>
                  <li>
                    Each person&apos;s accountability partner matches the other, so if Ben&apos;s AP is Mary then
                    Mary&apos;s AP is Ben.
                  </li>
                </ol>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      {(rows?.length ?? 0) > 0 && (
        <DataGrid
          rows={rows}
          columns={[
            { field: 'id', headerName: 'ID', width: 80, sortable: true },

            {
              field: 'participantName',
              headerName: 'Participant Name',
              flex: 1,
              sortable: true,
              renderCell: (params) => {
                if (params.row.id === 0) {
                  return (
                    <FormControl fullWidth size="small">
                      <InputLabel id={`participant-label-${params.id}`}>Participant Name</InputLabel>
                      <Select
                        labelId={`participant-label-${params.id}`}
                        label="Participant Name"
                        value={params.row.participantId || ''}
                        onChange={(e) => updateNewRowParticipant(Number(e.target.value))}
                      >
                        {(participants || []).map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }
                return <Typography>{params.row.participantName}</Typography>;
              },
            },

            {
              field: 'partnerName',
              headerName: 'Partner Name',
              flex: 1,
              sortable: true,
              renderCell: (params) => {
                if (params.row.id === 0) {
                  return (
                    <FormControl fullWidth size="small">
                      <InputLabel id={`partner-label-${params.id}`}>Partner Name</InputLabel>
                      <Select
                        labelId={`partner-label-${params.id}`}
                        label="Partner Name"
                        value={params.row.partnerId || ''}
                        onChange={(e) => updateNewRowPartner(Number(e.target.value))}
                      >
                        {(participants || []).map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }
                return <Typography>{params.row.partnerName}</Typography>;
              },
            },

            {
              field: 'actions',
              headerName: 'Actions',
              minWidth: 180,
              sortable: false,
              renderCell: (params) => {
                if (params.row.id === 0) {
                  return (
                    <Grid container gap={1}>
                      <Button variant="contained" onClick={handleCancelNewRow}>
                        Cancel
                      </Button>
                      <Button variant="contained" onClick={handleSaveNewRow}>
                        Save
                      </Button>
                    </Grid>
                  );
                }
                return (
                  <Button
                    variant="contained"
                    onClick={async (e) => {
                      e.preventDefault();
                      await deletePartnerRecordById(params.row.id);
                    }}
                  >
                    Delete
                  </Button>
                );
              },
            },
          ]}
          hideFooter
          disableColumnMenu
          sx={{
            fontFamily: 'Roboto, sans-serif',
            margin: '0 50px',
            // Vertically center everything
            '& .MuiDataGrid-row': { alignItems: 'center' },
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
            '& .MuiDataGrid-columnHeader': { display: 'flex', alignItems: 'center' },
          }}
        />
      )}
      <DialogActions>
        <Button onClick={handleAddRow}>Add Partner</Button>
        <Button onClick={deleteAllPartners}>Delete All Partners</Button>
        <Button onClick={closeDialog} variant={'text'}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
